/**
 * Readability Analyzer
 * Approximates readability scores like Yoast.
 */

const getWordCount = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
};

// Extract text content from basic HTML or simple text
const stripHtml = (html) => {
  if (!html) return "";
  return html.replace(/<[^>]*>?/gm, ' ');
};

export const analyzeReadability = (data) => {
  const { content = "" } = data;
  let score = 0;
  let maxScore = 10;
  const results = [];

  const cleanContent = stripHtml(content);
  if (!cleanContent.trim()) {
    return { score, maxScore, results: [] };
  }

  // 1. Sentence length
  // Split by periods, question marks, or exclamation points followed by space
  const sentences = cleanContent.match(/[^.!?]+[.!?]+/g) || [cleanContent];
  const totalSentences = sentences.length;
  
  if (totalSentences > 0) {
    const longSentences = sentences.filter(s => getWordCount(s) > 20);
    const longSentencePercentage = (longSentences.length / totalSentences) * 100;

    if (longSentencePercentage <= 25) {
      score += 4;
      results.push({ status: "success", text: `Sentence length is good (${longSentencePercentage.toFixed(1)}% are over 20 words).` });
    } else {
      results.push({ status: "warning", text: `${longSentencePercentage.toFixed(1)}% of sentences are over 20 words (recommended: < 25%).` });
    }
  }

  // 2. Passive voice (very basic heuristic)
  // Checks for "to be" verbs followed by an 'ed' verb
  const passiveRegex = /\b(am|is|are|was|were|be|been|being)\b\s+\w+ed\b/gi;
  const passiveMatches = cleanContent.match(passiveRegex) || [];
  const passiveLimit = totalSentences * 0.1; // roughly 10% of sentences

  if (passiveMatches.length <= passiveLimit) {
    score += 3;
    results.push({ status: "success", text: "Passive voice usage is low." });
  } else {
    results.push({ status: "warning", text: "Too much passive voice detected. Try using active voice." });
  }

  // 3. Subheading distribution (warning if > 300 words without a subheading)
  // Naive check: split by H2/H3 tags and check chunk lengths
  let chunks = [];
  if (content.match(/<h[2-6]/i)) {
    chunks = content.split(/<h[2-6][^>]*>.*?<\/h[2-6]>/gi);
  } else {
    chunks = [content];
  }
  
  const longChunks = chunks.filter(chunk => getWordCount(stripHtml(chunk)) > 300);
  if (longChunks.length === 0) {
    score += 3;
    results.push({ status: "success", text: "Good subheading distribution (no text sections over 300 words)." });
  } else {
    results.push({ status: "warning", text: "Some text sections are over 300 words. Add more subheadings." });
  }

  return { score, maxScore, results };
};
